import {
  ALARM_CLIP_ASSET_TYPE_ID,
  type AttachAlarmClipRequest,
  type AttachAlarmClipResponse,
} from '../../models/entities/alarm-clip.model';
import {
  SNAPSHOT_ASSET_TYPE_ID,
  type SnapshotRequest,
  type SnapshotResponse,
} from '../../models/entities/snapshot.model';
import type { ResolveSaveEventAssetResponse } from '../../models/entities/resolve-api.model';

const SNAPSHOT_DATA_URL_PREFIX = 'data:image/png;base64,';

function sanitizeSnapshotFileName(value: string | null | undefined): string | null {
  const safe = value
    ?.trim()
    .replace(/[^a-zA-Z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  return safe ? `${safe}.png` : null;
}

function withSnapshotDataUrl(dataBase64: string): string {
  const trimmed = dataBase64.trim();
  if (trimmed.startsWith('data:')) {
    return trimmed;
  }
  return `${SNAPSHOT_DATA_URL_PREFIX}${trimmed}`;
}

function sanitizeAssetName(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  const safe = trimmed.replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '');
  return safe || null;
}

function assetId(response: ResolveSaveEventAssetResponse): string | undefined {
  const id = response.asset?.id;
  if (id == null || id === '') {
    return undefined;
  }
  return String(id);
}

/**
 * Snapshot button → Resolve `SaveEventAssetRequest` (`assetTypeId` 0).
 * `capturedAt` is the NVR wall clock encoded as UTC
 * (`2026-10-14T22:57:35.000Z` means 22:57:35 on the recorder).
 */
export function buildSnapshotAssetRequest(
  dataBase64: string,
  cameraName: string,
  capturedAt = new Date().toISOString(),
): SnapshotRequest {
  const uuid = crypto.randomUUID();
  return {
    assetTypeId: SNAPSHOT_ASSET_TYPE_ID,
    image: {
      dataBase64: withSnapshotDataUrl(dataBase64),
      name: sanitizeSnapshotFileName(`${cameraName}-${uuid}`),
      timestamp: capturedAt,
    },
  };
}

/**
 * Attach-clip button → Resolve `SaveEventAssetRequest` (`assetTypeId` 1).
 * `capturedAt` is the clip-start NVR wall clock encoded as UTC, same as snapshots.
 * Resolve stores it on the asset and the gallery hover reads those UTC fields as the clip time.
 */
export function buildClipAssetRequest(
  alarmId: string,
  cameraName: string,
  capturedAt?: string | null,
): AttachAlarmClipRequest {
  const timestamp = capturedAt?.trim();
  return {
    assetTypeId: ALARM_CLIP_ASSET_TYPE_ID,
    clip: {
      alarmId,
      name: sanitizeAssetName(`${cameraName}-${alarmId}`),
      ...(timestamp ? { timestamp } : {}),
    },
  };
}

export function mapResolveAssetToSnapshotResponse(
  response: ResolveSaveEventAssetResponse,
): SnapshotResponse {
  return {
    success: true,
    snapshotId: assetId(response),
  };
}

export function mapResolveAssetToClipResponse(
  response: ResolveSaveEventAssetResponse,
): AttachAlarmClipResponse {
  return {
    success: true,
    clipId: assetId(response),
  };
}
